import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { filterTemplatesForScreen, isApprovedMetaTemplate } from "../../utils/templateAppUsage";
import { inferTemplateVariableSlots } from "../../utils/templateVariableSamples";
import {
  ENQUIRY_TEMPLATE_FIELDS,
  defaultEnquiryVariablePaths,
  emptyTemplateVariables,
  templateDisplayName,
} from "./helpers";

function enquiryTemplatePicker(templates) {
  const tagged = filterTemplatesForScreen(templates, "email_enquiry");
  if (tagged.length) return { list: tagged, fallback: false };
  const approved = (templates || []).filter(isApprovedMetaTemplate);
  return { list: approved, fallback: approved.length > 0 };
}

export default function RuleDialog({
  open,
  editing,
  form,
  setForm,
  templates,
  saving,
  onClose,
  onSave,
}) {
  const picker = enquiryTemplatePicker(templates);
  const selected = picker.list.find((t) => (t.getId?.() || t.id) === form.template_id);
  const slots = selected ? inferTemplateVariableSlots(selected) : emptyTemplateVariables();
  const mappingSections = ["header", "body", "footer"].filter(
    (section) => Object.keys(slots[section] || {}).length > 0,
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? "Edit rule" : "Create rule"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Rule name"
            fullWidth
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Parser</InputLabel>
            <Select
              label="Parser"
              value={form.parser_type}
              onChange={(e) => {
                const parser_type = e.target.value;
                setForm((f) => ({
                  ...f,
                  parser_type,
                  create_lead: parser_type === "indiamart_enquiry" ? true : f.create_lead,
                }));
              }}
            >
              <MenuItem value="indiamart_enquiry">IndiaMART enquiry (name/email/mobile)</MenuItem>
              <MenuItem value="phones_only">Phones only</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Subject match</InputLabel>
            <Select
              label="Subject match"
              value={form.subject_match_type}
              onChange={(e) => setForm((f) => ({ ...f, subject_match_type: e.target.value }))}
            >
              <MenuItem value="contains">Contains</MenuItem>
              <MenuItem value="exact">Exact</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Subject pattern"
            fullWidth
            required
            value={form.subject_pattern}
            onChange={(e) => setForm((f) => ({ ...f, subject_pattern: e.target.value }))}
            helperText='e.g. "Enquiry for" for IndiaMART subjects'
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.create_lead}
                onChange={(e) => setForm((f) => ({ ...f, create_lead: e.target.checked }))}
              />
            }
            label="Create lead"
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.send_whatsapp}
                onChange={(e) => setForm((f) => ({ ...f, send_whatsapp: e.target.checked }))}
              />
            }
            label="Send WhatsApp"
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.send_email}
                onChange={(e) => setForm((f) => ({ ...f, send_email: e.target.checked }))}
              />
            }
            label="Send email to buyer"
          />

          {form.send_whatsapp && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>WhatsApp action</InputLabel>
                <Select
                  label="WhatsApp action"
                  value={form.action_type}
                  onChange={(e) => setForm((f) => ({ ...f, action_type: e.target.value }))}
                >
                  <MenuItem value="template">WhatsApp template</MenuItem>
                  <MenuItem value="text">Fixed text</MenuItem>
                </Select>
              </FormControl>
              {form.action_type === "template" ? (
                <>
                  <FormControl fullWidth size="small">
                    <InputLabel>Template</InputLabel>
                    <Select
                      label="Template"
                      value={form.template_id || ""}
                      onChange={(e) => {
                        const template_id = e.target.value;
                        const next = picker.list.find((t) => (t.getId?.() || t.id) === template_id);
                        const nextSlots = next
                          ? inferTemplateVariableSlots(next)
                          : emptyTemplateVariables();
                        setForm((f) => ({
                          ...f,
                          template_id,
                          template_variables: defaultEnquiryVariablePaths(nextSlots),
                        }));
                      }}
                    >
                      {picker.list.length === 0 && (
                        <MenuItem value="" disabled>
                          No approved templates. Create one and tag it Email enquiry.
                        </MenuItem>
                      )}
                      {picker.list.length > 0 && <MenuItem value="">Select a template</MenuItem>}
                      {picker.list.map((t) => (
                        <MenuItem key={t.getId?.() || t.id} value={t.getId?.() || t.id}>
                          {templateDisplayName(t)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selected && mappingSections.length > 0 && (
                    <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.200" }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Map template variables to enquiry fields
                      </Typography>
                      {mappingSections.map((section) => {
                        const keys = Object.keys(slots[section] || {}).sort((a, b) => Number(a) - Number(b));
                        return (
                          <Box key={section} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, textTransform: "capitalize" }}>
                              {section}
                            </Typography>
                            {keys.map((key) => (
                              <Box key={`${section}-${key}`} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <Typography variant="body2" sx={{ minWidth: 48, fontFamily: "monospace", fontWeight: 700 }}>
                                  {`{{${key}}}`}
                                </Typography>
                                <FormControl fullWidth size="small">
                                  <Select
                                    displayEmpty
                                    value={form.template_variables?.[section]?.[key] || ""}
                                    onChange={(e) =>
                                      setForm((f) => ({
                                        ...f,
                                        template_variables: {
                                          ...(f.template_variables || emptyTemplateVariables()),
                                          [section]: {
                                            ...(f.template_variables?.[section] || {}),
                                            [key]: e.target.value,
                                          },
                                        },
                                      }))
                                    }
                                  >
                                    <MenuItem value=""><em>Select field…</em></MenuItem>
                                    {ENQUIRY_TEMPLATE_FIELDS.map((field) => (
                                      <MenuItem key={field.value} value={field.value}>{field.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            ))}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              ) : (
                <TextField
                  label="WhatsApp message text"
                  fullWidth
                  multiline
                  minRows={2}
                  required
                  value={form.message_text}
                  onChange={(e) => setForm((f) => ({ ...f, message_text: e.target.value }))}
                />
              )}
            </>
          )}

          {form.send_email && (
            <>
              <TextField
                label="Email subject"
                fullWidth
                required
                value={form.email_subject}
                onChange={(e) => setForm((f) => ({ ...f, email_subject: e.target.value }))}
              />
              <TextField
                label="Email body"
                fullWidth
                multiline
                minRows={4}
                required
                value={form.email_body}
                onChange={(e) => setForm((f) => ({ ...f, email_body: e.target.value }))}
              />
            </>
          )}

          <TextField
            label="Priority"
            type="number"
            size="small"
            sx={{ maxWidth: 160 }}
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            }
            label="Enabled"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : editing ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
